import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import axios, { API } from "../lib/api";
import useLive from "../hooks/useLive";
import useLenis from "../hooks/useLenis";
import { useLang } from "../lib/i18n";
import { Nav } from "../components/site/Nav";
import { Footer } from "../components/site/Footer";
import { OrderDialog } from "../components/site/OrderDialog";
import { ChatWidget } from "../components/site/ChatWidget";
import { MaskLines, Reveal } from "../components/site/Reveal";
import { SectionBg } from "../components/site/SectionBg";
import { ReportCards, ReportsTable } from "../components/site/ReportsList";

const avg = (rows, k) => {
  const v = rows.map((r) => r[k]).filter((x) => x != null);
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
};

export default function Reports() {
  useLenis();
  const { t, lang } = useLang();
  const { products, closures } = useLive(30000);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("batch") || "");
  const [prod, setProd] = useState("all");
  const [order, setOrder] = useState({ open: false, slug: null, prefill: null });
  const openOrder = (slug = null) => setOrder({ open: true, slug: typeof slug === "string" ? slug : null, prefill: null });

  useEffect(() => {
    axios.get(`${API}/reports`).then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => setRows([]));
  }, []);

  const names = Object.fromEntries(products.map((p) => [p.slug, p.name]));
  const shown = useMemo(
    () => rows.filter((r) => (prod === "all" || r.product_slug === prod) && r.batch_no.toLowerCase().includes(q.trim().toLowerCase())),
    [rows, prod, q]
  );
  const dateFmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { day: "numeric", month: "short", year: "numeric" });
  const m = avg(shown, "moisture_pct"), a = avg(shown, "ash_pct"), g = avg(shown, "gcv_kcal");
  const stats = [[t("rep.avgM"), m == null ? "—" : `${m.toFixed(1)}%`], [t("rep.avgA"), a == null ? "—" : `${a.toFixed(1)}%`], [t("rep.avgG"), g == null ? "—" : `${Math.round(g)} kcal/kg`]];

  return (
    <div className="grain relative" data-testid="reports-page">
      <Nav onOrder={openOrder} />
      <main className="relative pt-[120px] pb-24 overflow-hidden">
        <SectionBg src="/images/hero-pellets.jpg" opacity={0.35} testId="reports-bg" />
        <section className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <p className="eyebrow mb-5">{t("rep.eyebrow")}</p>
              <h1 key={lang} className="font-display font-bold tracking-[-0.04em] leading-[0.92] text-6xl sm:text-7xl lg:text-8xl">
                <MaskLines lines={[t("rep.h1"), <span key="a" className="font-serif-i font-normal text-[var(--husk)]">{t("rep.accent")}</span>]} delay={0.2} />
              </h1>
            </div>
            <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }} className="lg:col-span-5 text-[var(--sage)] text-base sm:text-lg leading-relaxed">
              {t("rep.sub")}
            </motion.p>
          </div>
          <Reveal className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div data-testid="reports-count" className="rounded-2xl border hairline bg-black/30 p-4 sm:p-5 col-span-2 lg:col-span-1">
              <p className="font-display text-3xl font-bold">{shown.length}</p>
              <p className="font-mono text-xs text-[var(--sage)]">{t("rep.count", { n: shown.length })}</p>
            </div>
            {stats.map(([k, v]) => (
              <div key={k} className="rounded-2xl border hairline bg-black/30 p-4 sm:p-5">
                <p className="font-display text-2xl sm:text-3xl font-bold text-[var(--husk)]">{v}</p>
                <p className="font-mono text-xs text-[var(--sage)]">{k}</p>
              </div>
            ))}
          </Reveal>
          <div className="mt-8 flex flex-col md:flex-row md:items-center gap-3">
            <label className="relative md:w-80">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--sage)]" />
              <input data-testid="reports-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("rep.search")} className="h-12 w-full rounded-full bg-black/30 border hairline pl-11 pr-4 text-sm text-[var(--cream)] placeholder:text-[var(--sage)] outline-none focus:border-[var(--mint)]" />
            </label>
            <div className="flex flex-wrap gap-2">
              {[["all", t("rep.all")], ...products.map((p) => [p.slug, p.name])].map(([k, label]) => (
                <button key={k} data-testid={`reports-filter-${k}`} onClick={() => setProd(k)} className={`h-10 rounded-full px-4 text-sm transition-colors ${prod === k ? "bg-[var(--husk)] text-[var(--forest)] font-semibold" : "border hairline hover:bg-white/5"}`}>{label}</button>
              ))}
            </div>
          </div>
          <div className="mt-6">
            {shown.length === 0 ? (
              <p data-testid="reports-empty" className="rounded-[24px] border hairline bg-black/30 p-8 text-center text-[var(--sage)]">{t("rep.empty")}</p>
            ) : (
              <>
                <ReportsTable rows={shown} names={names} dateFmt={dateFmt} />
                <ReportCards rows={shown} names={names} dateFmt={dateFmt} />
              </>
            )}
          </div>
        </section>
      </main>
      <Footer onOrder={openOrder} />
      <OrderDialog open={order.open} onOpenChange={(v) => setOrder((o) => ({ ...o, open: v }))} products={products} initialSlug={order.slug} prefill={order.prefill} closures={closures} />
      <ChatWidget onDraft={(d) => setOrder({ open: true, slug: products.some((p) => p.slug === d?.product_slug) ? d.product_slug : null, prefill: d })} />
    </div>
  );
}
